import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { Button, Form, Offcanvas, Spinner, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import keywordsApi, {
  useLazyGetSettingsQuery,
  useSetSettingsMutation
} from '@/app/KeywordTracker/api/keywordsApi';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { Formik } from 'formik';
import { errorMessage, successMessage } from '@/utils/toast';
import Main from './keywords-settings/Main';
import Notifications from './keywords-settings/Notifications';
import KeywordSettingsTabs from './keywords-settings/KeywordSettingsTabs';
import _ from 'lodash';
import useDomain from '@/hooks/useDomain';
import { useDispatch } from 'react-redux';
import UpdateSettingsSuccessWarningModal from './UpdateSettingsSuccessWarningModal';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import type { DrawerShowProps, TrackerSettings } from './types';

const getWeeklyDaysExceededMessage = (weeklyUpdateDays: number) =>
  `در طرح شما فقط ${weeklyUpdateDays} روز قابل انتخاب هست. تعداد روزهای انتخاب‌شده مجاز نیست؛ لطفا آن را به ${weeklyUpdateDays} روز تغییر دهید.`;

const getRandomInactiveUpdateDay = (): string[] => [String(_.random(0, 5))];

const initSettings: TrackerSettings = {
  enable_notification_by_email: true,
  enable_notification_by_telegram: true,
  enable_notification_by_bale: true,
  set_settings_for_all_domains: false,
  enable_automatic_update: false,
  notif_via_email_to_access_levels: [],
  notif_via_telegram_to_access_levels: [],
  notif_via_bale_to_access_levels: [],
  notification_time: 9,
  updated_on_week: getRandomInactiveUpdateDay()
};

const UpdateSettingsDrawer = ({ show, setShow }: DrawerShowProps) => {
  const { domain, domain_slug } = useDomain();
  const [getSettings, { isFetching: isSettingsLoading }] =
    useLazyGetSettingsQuery();
  const [setSettings] = useSetSettingsMutation();
  const [settingsReady, setSettingsReady] = useState(false);
  const dispatch = useDispatch();
  const [settingState, setSettingState] = useState<TrackerSettings>(initSettings);
  const [weeklyUpdateDays, setWeeklyUpdateDays] = useState<number | null>(null);
  const [showSuccessWarningModal, setShowSuccessWarningModal] = useState(false);

  const handleSuccessWarningModalClose = () => {
    setShowSuccessWarningModal(false);
  };

  useEffect(() => {
    if (show) {
      setSettingsReady(false);

      getSettings(domain_slug)
        .unwrap()
        .then(result => {
          const newSettings = { ...initSettings };

          Object.entries(result.data).forEach(([key, value]) => {
            if (key === 'updated_on_week') {
              if (!result.data.enable_automatic_update) {
                newSettings[key] = getRandomInactiveUpdateDay();
              } else {
                result.data.updated_on_week &&
                  result.data.updated_on_week.length > 0
                  ? (newSettings[key] = value)
                  : (newSettings[key] = []);
              }
            } else {
              newSettings[key] = value;
            }
          });

          setSettingState(newSettings);
          console.log('result.data.weekly_updates', result.data.weekly_updates);
          setWeeklyUpdateDays(result.data.weekly_updates ?? null);
          setSettingsReady(true);
        })
        .catch(err => console.error(err));
    }
  }, [show]);

  return (
    <>
    <Offcanvas
      onShow={() => {
        const offcanvasElement = document.querySelector('.offcanvas');
        if (offcanvasElement) {
          offcanvasElement.removeAttribute('tabindex');
        }
      }}
      show={show}
      onHide={() => {
        setShow(false);
      }}
      style={{ backgroundColor: 'var(--falcon-body-bg)' }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <div className="m-0">
              تنظیمات دامنه&nbsp;
              <strong>{domain}</strong>
              {/* <HelpTooltip html={'23'} /> */}
            </div>
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="pb-5 pt-0">
        <Formik
          validationSchema={Yup.object().shape({
            enable_notification_by_email: Yup.boolean().required(),
            enable_notification_by_telegram: Yup.boolean().required(),
            enable_notification_by_bale: Yup.boolean().required(),
            set_settings_for_all_domains: Yup.boolean().required(),
            enable_automatic_update: Yup.boolean().required(),
            notification_time: Yup.number()
              .min(0)
              .max(23)
              .required('ساعت اعلان الزامی است.'),
            updated_on_week: Yup.array()
              .of(Yup.string())
              .when('enable_automatic_update', {
                is: true,
                then: schema =>
                  schema.test(
                    'non-empty-updates',
                    'در صورت فعال بودن به‌روزرسانی خودکار، حداقل یک روز هفته باید انتخاب شود.',
                    value =>
                      Array.isArray(value) &&
                      value.filter(
                        item => typeof item === 'string' && item.trim() !== ''
                      ).length > 0
                  ),
                otherwise: schema => schema.notRequired()
              }),
            notif_via_email_to_access_levels: Yup.array()
              .of(Yup.string()) // فقط بررسی می‌کنیم آیتم‌ها استرینگ باشن
              .when('enable_notification_by_email', {
                is: true,
                then: schema =>
                  schema.test(
                    'non-empty-strings',
                    'حداقل یک سطح دسترسی انتخاب کنید',
                    value =>
                      Array.isArray(value) &&
                      value.filter(
                        item => typeof item === 'string' && item.trim() !== ''
                      ).length > 0
                  ),
                otherwise: schema => schema.notRequired()
              }),

            notif_via_telegram_to_access_levels: Yup.array()
              .of(Yup.string())
              .when('enable_notification_by_telegram', {
                is: true,
                then: schema =>
                  schema.test(
                    'non-empty-strings',
                    'حداقل یک سطح دسترسی انتخاب کنید',
                    value =>
                      Array.isArray(value) &&
                      value.filter(
                        item => typeof item === 'string' && item.trim() !== ''
                      ).length > 0
                  ),
                otherwise: schema => schema.notRequired()
              }),

            notif_via_bale_to_access_levels: Yup.array()
              .of(Yup.string())
              .when('enable_notification_by_bale', {
                is: true,
                then: schema =>
                  schema.test(
                    'non-empty-strings',
                    'حداقل یک سطح دسترسی انتخاب کنید',
                    value =>
                      Array.isArray(value) &&
                      value.filter(
                        item => typeof item === 'string' && item.trim() !== ''
                      ).length > 0
                  ),
                otherwise: schema => schema.notRequired()
              })
          })}
          onSubmit={async (values, action) => {
            const data = {
              ...values,
              notif_via_telegram_to_access_levels:
                values.notif_via_telegram_to_access_levels.filter(Boolean)
                  .length > 0
                  ? values.notif_via_telegram_to_access_levels.filter(Boolean)
                  : null,
              notif_via_email_to_access_levels:
                values.notif_via_email_to_access_levels.filter(Boolean).length >
                  0
                  ? values.notif_via_email_to_access_levels.filter(Boolean)
                  : null,
              notif_via_bale_to_access_levels:
                values.notif_via_bale_to_access_levels.filter(Boolean).length >
                  0
                  ? values.notif_via_bale_to_access_levels.filter(Boolean)
                  : null
            };
            setSettings(data)
              .then(() => {
                dispatch(keywordsApi.util.invalidateTags(['tracker_info']));
                successMessage('تنظیمات به‌روزرسانی باموفقیت تغییر کرد.');
                setShowSuccessWarningModal(true);
              })
              .catch(err => {
                errorMessage('خطای ناشناخته در ثبت تنظیمات به‌روزرسانی.');
                console.error('Error submitting auto settings:', err);
              });

            setSettingState(initSettings);
            setShow(false);
          }}
          initialValues={settingState}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue
          }) => {
            const isWeeklyDaysExceeded =
              weeklyUpdateDays != null &&
              weeklyUpdateDays < values.updated_on_week.length;
            const weeklyDaysExceededTooltip = isWeeklyDaysExceeded
              ? getWeeklyDaysExceededMessage(weeklyUpdateDays)
              : '';

            return !isSettingsLoading && settingsReady ? (
              <Form
                onSubmit={e => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                <Tab.Container defaultActiveKey="updateSettings">
                  <KeywordSettingsTabs />
                  <Tab.Content>
                    <Main
                      weeklyUpdateDays={weeklyUpdateDays}
                      updateDomainHistory={settingState.update_domain_history}
                    />

                    <Notifications />
                  </Tab.Content>
                </Tab.Container>
                <hr className="my-2" />
                <div className="d-flex gap-2 align-items-center">
                  <Form.Check
                    type="checkbox"
                    id="set_settings_for_all_domains"
                    name="set_settings_for_all_domains"
                    label={
                      <div className="fs--1 cursor-pointer">
                        تنظیمات فوق برای همه دامنه ها اعمال شود.{' '}
                      </div>
                    }
                    checked={values.set_settings_for_all_domains}
                    onChange={({ target }) =>
                      setFieldValue(
                        'set_settings_for_all_domains',
                        target.checked
                      )
                    }
                  />
                </div>
                {/* دکمه های آخر صفحه */}
                <div className="position-sm-absolute bottom-0 d-flex gap-2 end-0 justify-content-end p-sm-4 pb-7 pt-2">
                  <Button
                    className="d-flex gap-2 align-items-center"
                    variant="outline-secondary"
                    onClick={() => setShow(false)}
                  >
                    <FontAwesomeIcon size="lg" icon="times" />
                    لغو
                  </Button>
                  <ChildrensTooltip
                    placement="top"
                    html={weeklyDaysExceededTooltip}
                  >
                    <Button
                      className="d-flex gap-2 align-items-center border-0"
                      type="submit"
                      variant="falcon-primary"
                      disabled={isWeeklyDaysExceeded}
                    >
                      <FontAwesomeIcon size="lg" icon="save" />
                      ذخیره تنظیمات
                    </Button>
                  </ChildrensTooltip>
                </div>
              </Form>
            ) : (
              <div className="d-flex mt-3 align-items-center justify-content-center w-100 h-100">
                <div className="d-flex align-items-center gap-2">
                  <Spinner size="sm" />
                  درحال دریافت تنظیمات
                </div>
              </div>
            );
          }}
        </Formik>
      </Offcanvas.Body>

      <style>
        {/* استایل کلی دراور */}
        {`
 .offcanvas{
     width:500px !important;
      max-width:100% !important;
    }
  .offcanvas img {
    margin: 0 auto;
    max-width: 100%;
    height: auto;
    display: flex;
    }
 `}
      </style>
    </Offcanvas>
    <UpdateSettingsSuccessWarningModal
      show={showSuccessWarningModal}
      onClose={handleSuccessWarningModalClose}
    />
    </>
  );
};

export default UpdateSettingsDrawer;
